<template>
  <div
    class="wt2-player-video"
    :class="{ 'visible-control': isVisibleControl && !hideControls }"
    @mouseleave="onVideoLeave"
    @mousemove="onVideoMove"
  >
    <div
      class="wt2-player-video-container"
      @click="togglePlay('Play on player')"
    >
      <slot></slot>
    </div>

    <div v-show="!showPlayIcon && showBufferIcon" class="buffer-indicator">
      <buffer-indicator />
    </div>

    <transition name="fade">
      <div
        v-show="showPlayIcon"
        class="player-indicator wt2-color-negative wt2-pointer"
      >
        <i class="wticon wticon-play_4" />
      </div>
    </transition>

    <transition name="fade">
      <div
        v-show="showPauseIcon"
        class="player-indicator wt2-color-negative wt2-pointer"
      >
        <i class="wticon wticon-pause2" />
      </div>
    </transition>

    <div ref="controls" />

    <slot name="overlay-layer" />
  </div>
</template>

<script>
import app from 'app';
import Vue from 'vue';
import PlayerControlPanel from 'v/PlayerControlPanel.vue';
import BufferIndicator from '../../../img/buffer-indicator.vue.svg';

export default Vue.extend({
  components: {
    BufferIndicator,
  },

  props: {
    togglePlayOnSpace: {
      type: Boolean,
      default: true,
    },

    supportFullscreenMode: {
      type: Boolean,
      default: true,
    },

    timeoutControlTime: {
      type: Number,
      default: 2000,
    },

    autoPlay: {
      type: Boolean,
      default: true,
    },

    hideControlsWhenNotPlaying: {
      type: Boolean,
      default: false,
    },

    hideControls: {
      type: Boolean,
      default: false,
    },
  },

  beforeDestroy() {
    const videoVN = this.$slots.default[0];

    if (videoVN) {
      const videoEl = videoVN.elm;
      videoEl.removeEventListener('loadstart', this.onLoadStart);
      videoEl.removeEventListener('loadedmetadata', this.onLoadMetaData);
      videoEl.removeEventListener('timeupdate', this.onTimeUpdate);
      videoEl.removeEventListener('playing', this.onPlaying);
      videoEl.removeEventListener('pause', this.onPause);
      videoEl.removeEventListener('seeking', this.onSeeking);
      videoEl.removeEventListener('seeked', this.onSeeked);
      videoEl.removeEventListener('canplay', this.onCanPlay);
      videoEl.removeEventListener('volumechange', this.onVolChange);
      videoEl.removeEventListener('durationchange', this.onDurationChange);
      // destroy video
      videoEl.src = '';
    }

    if (this.controlPanel) {
      this.controlPanel.$destroy();
      this.controlPanel = null;
    }

    if (this.timeoutControl) {
      this.cancelTimeout();
    }

    app.keyboardAccessibility.remove(`wt-player-video-${this._uid}`);
  },

  data() {
    return {
      controlPanel: null,
      isVisibleControl: true,
      onLoadStart: null,
      onLoadMetaData: null,
      onTimeUpdate: null,
      onPlaying: null,
      onPause: null,
      onSeeking: null,
      onSeeked: null,
      onCanPlay: null,
      onVolChange: null,
      onDurationChange: null,
      timeoutControl: null,
      showBufferIcon: true,
      showPlayIcon: false,
      showPauseIcon: false,
      numOfPlays: 0,
    };
  },

  created() {
    this.$_debouncedBufferIndicatorOn = _.debounce(
      function () {
        this.showBufferIcon = true;
      },
      app.TIME.MENTAL.λ,
      { leading: false, trailing: true }
    );
    this.$_debouncedBufferIndicatorOff = _.debounce(
      function () {
        this.showBufferIcon = false;
      },
      app.TIME.MENTAL.λ,
      { leading: false, trailing: true }
    );
  },

  mounted() {
    // slot vnode (should be a video element)
    const videoVN = this.$slots.default[0];

    if (!videoVN) {
      this.$log('wt-player-video: a slot with a video element is missing!');
      return;
    }

    const videoEl = videoVN.elm;
    const videoComponentEl = this.$el;
    const initialVolume = videoEl.volume;
    const initialTime = videoEl.currentTime;
    const supportFullscreenMode = this.supportFullscreenMode;
    let canPlay = false;

    // create instance of player controls and mount it to placeholder
    this.controlPanel = new PlayerControlPanel({
      el: this.$refs.controls,

      type: supportFullscreenMode ? 'regular-plus' : 'regular',

      data() {
        return {
          volume: {
            current: initialVolume,
          },
          time: {
            current: initialTime,
          },
        };
      },

      created() {
        // wire panel with player

        // listen to events from panel and wire video
        this.$on('togglePlay', () => {
          this.togglePlay('Play on panel');
        });

        this.$on('seek', ({ time }) => {
          time = ((time * 1e3) | 0) / 1e3;
          if (time !== videoEl.currentTime) {
            videoEl.currentTime = time;
          }
        });

        this.$on('volume', (volume) => {
          videoEl.volume = ((volume * 1e3) | 0) / 1e3;
        });

        this.$on('toggleFullscreen', () => {
          const requestFullscreen =
            videoComponentEl.requestFullscreen ||
            videoComponentEl.webkitRequestFullscreen;

          const fullscreenElement =
            document.fullscreenElement || document.webkitFullscreenElement;

          const exitFullscreen =
            document.exitFullscreen || document.webkitExitFullscreen;

          if (!fullscreenElement) {
            if (requestFullscreen) {
              requestFullscreen.call(videoComponentEl);
            } else {
              this.$log('wt-player-video: fullscreen api is not supported!');
            }
          } else {
            if (exitFullscreen) {
              exitFullscreen.call(document);
            }
          }
        });
      },

      methods: {
        togglePlay(source) {
          if (!canPlay) {
            return;
          }
          if (videoEl.paused) {
            videoEl.play().catch(() => 0);
            this.$emit('play', source);
          } else {
            videoEl.pause();
          }
        },

        isVideoPaused() {
          return videoEl.paused;
        },
      },
    });

    this.controlPanel.$on('play', (source) => {
      this.$emit('play', source);
    });

    this.onLoadStart = () => {
      // reset video
      this.reset(videoEl);
    };

    videoEl.addEventListener('loadstart', this.onLoadStart);

    // listen to events from video and wire panel
    this.onLoadMetaData = () => {
      this.controlPanel.time.total = videoEl.duration;
    };

    videoEl.addEventListener('loadedmetadata', this.onLoadMetaData);

    this.onTimeUpdate = () => {
      this.controlPanel.time.current = videoEl.currentTime;
    };

    videoEl.addEventListener('timeupdate', this.onTimeUpdate);

    this.onPlaying = () => {
      this.$emit('played', this.numOfPlays++);
      this.controlPanel.playing = true;
      this.createTimeout();
      this.isVisibleControl = true;
      this.showPauseIcon = false;
    };

    videoEl.addEventListener('play', this.onPlaying);

    this.onPause = () => {
      this.$emit('paused');
      this.controlPanel.playing = false;
      this.cancelTimeout();
      this.isVisibleControl = true;
    };

    videoEl.addEventListener('pause', this.onPause);

    this.onSeeking = () => {
      this.$_debouncedBufferIndicatorOff.cancel();
      this.$_debouncedBufferIndicatorOn();
    };

    videoEl.addEventListener('seeking', this.onSeeking);

    this.onSeeked = () => {
      this.$_debouncedBufferIndicatorOn.cancel();
      this.$_debouncedBufferIndicatorOff();
    };

    videoEl.addEventListener('seeked', this.onSeeked);

    this.onVolChange = (e) => {
      this.controlPanel &&
        this.controlPanel.volume &&
        (this.controlPanel.volume.current = e.target.volume);
    };

    videoEl.addEventListener('volumechange', this.onVolChange);

    this.onDurationChange = () => {
      this.controlPanel.time.total = videoEl.duration;
    };

    videoEl.addEventListener('durationchange', this.onDurationChange);

    this.onCanPlay = () => {
      this.showBufferIcon = false;
      canPlay = true;
      // manual autoplay because sometimes the native attribute doesn't work properly
      if (this.autoPlay) {
        videoEl.play && videoEl.play().catch(() => 0);
      }
    };

    videoEl.addEventListener('canplay', this.onCanPlay, { once: true });

    app.keyboardAccessibility.add(`wt-player-video-${this._uid}`, [
      {
        on: 'keydown',
        key: $.ui.keyCode.SPACE,
        run: (e) => {
          if (!$(e.target).isEditable()) {
            e.preventDefault();
            if (this.togglePlayOnSpace) {
              this.togglePlay('Keyboard');
            }
          }
        },
      },
    ]);
  },

  methods: {
    togglePlay(source) {
      if (this.controlPanel) {
        if (!this.controlPanel.playing) {
          if (!(this.numOfPlays === 0 && this.autoPlay)) {
            this.showPlayIcon = true;
            _.delay(() => {
              this.showPlayIcon = false;
            }, 250);
          }
        } else {
          this.showPauseIcon = true;
          _.delay(() => {
            this.showPauseIcon = false;
          }, 250);
        }

        this.controlPanel.togglePlay(source);
      }
    },

    createTimeout() {
      this.timeoutControl = setTimeout(() => {
        this.isVisibleControl = false;
      }, this.timeoutControlTime);
    },

    cancelTimeout() {
      clearTimeout(this.timeoutControl);
    },

    reset(videoEl) {
      this.showBufferIcon = true;
      if (this.controlPanel) {
        this.controlPanel.playing = false;
        this.controlPanel.time.total = 0;
      }
      if (this.autoPlay) {
        videoEl.play && videoEl.play().catch(() => 0);
      }
    },

    isVideoPlaying() {
      return this.controlPanel && !this.controlPanel.isVideoPaused();
    },

    onVideoMove(e) {
      this.cancelTimeout();
      this.isVisibleControl = true;

      // if it's not the video, should preserve the controls
      if (/VIDEO/.test(e.target.tagName) && this.isVideoPlaying()) {
        this.createTimeout();
      }
    },

    onVideoLeave() {
      this.cancelTimeout();

      if (this.hideControlsWhenNotPlaying || this.isVideoPlaying()) {
        this.isVisibleControl = false;
      }
    },
  },
});
</script>
